import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { db, storage } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { FaEdit, FaTrash, FaPlus, FaSave, FaTimes } from 'react-icons/fa';
import './MyLinkTree.css';

const MyLinkTree = () => {
    const { isAdmin } = useContext(AuthContext);
    const [affiliateLinks, setAffiliateLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [editingLink, setEditingLink] = useState(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [copiedCodes, setCopiedCodes] = useState({});
    
    // New link template
    const emptyLink = {
        title: '',
        description: '',
        code: '',
        imageUrl: '',
        link: '',
        discountPercent: ''
    };
    
    // Form state for editing
    const [formData, setFormData] = useState(emptyLink);
    
    // Fetch affiliate links from Firestore
    useEffect(() => {
        const q = query(collection(db, 'affiliate_links'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const links = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setAffiliateLinks(links);
            setLoading(false);
        });
        
        return () => unsubscribe();
    }, []);
    
    // Handle file upload
    const handleImageUpload = async (file) => {
        if (!file) return null;
        
        try {
            setUploading(true);
            const path = `affiliate_images/${Date.now()}_${file.name}`;
            const storageRef = ref(storage, path);
            
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            
            return { url: downloadURL, path };
        } catch (err) {
            console.error('Error uploading image:', err);
            setError(`Upload failed: ${err.message}`);
            return null;
        } finally {
            setUploading(false);
        }
    };
    
    // Add a new affiliate link
    const handleAddLink = async (e) => {
        e.preventDefault();
        
        try {
            setError(null);
            
            // Upload image if provided
            let imageData = { url: '', path: '' };
            const fileInput = document.getElementById('affiliate-image-upload');
            
            if (fileInput.files.length > 0) {
                const result = await handleImageUpload(fileInput.files[0]);
                if (result) {
                    imageData = result;
                } else {
                    setError("Image upload failed. Please try again.");
                    return;
                }
            }
            
            // Add to Firestore
            await addDoc(collection(db, 'affiliate_links'), {
                title: formData.title,
                description: formData.description,
                code: formData.code || '',
                imageUrl: imageData.url || '',
                imagePath: imageData.path || '',
                link: formData.link || '',
                discountPercent: formData.discountPercent || '',
                createdAt: new Date().toISOString()
            });
            
            // Reset form
            setFormData(emptyLink);
            setIsAddingNew(false);
            
        } catch (err) {
            console.error('Error adding link:', err);
            setError(`Failed to add: ${err.message}`);
        }
    };
    
    // Update an existing link
    const handleUpdateLink = async (e) => {
        e.preventDefault();
        
        if (!editingLink) return;
        
        try {
            setError(null);
            
            // Get reference to the document
            const linkRef = doc(db, 'affiliate_links', editingLink);
            
            // Check if there's a new image upload
            let newImageData = null;
            const fileInput = document.getElementById('affiliate-image-upload');
            
            if (fileInput.files.length > 0) {
                newImageData = await handleImageUpload(fileInput.files[0]);
                
                if (!newImageData) {
                    setError("Image upload failed. Please try again.");
                    return;
                }
                
                // Delete old image if exists
                if (formData.imagePath) {
                    try {
                        const oldImageRef = ref(storage, formData.imagePath);
                        await deleteObject(oldImageRef);
                    } catch (deleteErr) {
                        console.warn('Could not delete old image:', deleteErr);
                    }
                }
            }
            
            // Update document
            await updateDoc(linkRef, {
                title: formData.title,
                description: formData.description,
                code: formData.code || '',
                link: formData.link || '',
                discountPercent: formData.discountPercent || '',
                ...(newImageData ? {
                    imageUrl: newImageData.url,
                    imagePath: newImageData.path,
                } : {})
            });
            
            // Reset edit state
            setEditingLink(null);
            setFormData(emptyLink);
            
        } catch (err) {
            console.error('Error updating link:', err);
            setError(`Failed to update: ${err.message}`);
        }
    };
    
    // Delete an affiliate link
    const handleDeleteLink = async (linkId, imagePath) => {
        if (!confirm('Are you sure you want to delete this affiliate link?')) return;
        
        try {
            setError(null);
            
            // Delete from Firestore
            await deleteDoc(doc(db, 'affiliate_links', linkId));
            
            // Delete image from storage if exists
            if (imagePath) {
                try {
                    const imageRef = ref(storage, imagePath);
                    await deleteObject(imageRef);
                } catch (deleteErr) {
                    console.warn('Could not delete image:', deleteErr);
                }
            }
            
        } catch (err) {
            console.error('Error deleting link:', err);
            setError(`Failed to delete: ${err.message}`);
        }
    };
    
    // Start editing an existing link
    const handleEdit = (link) => {
        setEditingLink(link.id);
        setFormData({
            title: link.title,
            description: link.description,
            code: link.code || '',
            imageUrl: link.imageUrl || '',
            imagePath: link.imagePath || '',
            link: link.link || '',
            discountPercent: link.discountPercent || ''
        });
        setIsAddingNew(false);
    };
    
    // Start adding a new link
    const startAddNew = () => {
        setIsAddingNew(true);
        setEditingLink(null);
        setFormData(emptyLink);
    };
    
    // Cancel editing/adding
    const cancelEdit = () => {
        setEditingLink(null);
        setIsAddingNew(false);
        setFormData(emptyLink);
    };
    
    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    // Copy discount code to clipboard
    const copyDiscountCode = (code, id) => {
        navigator.clipboard.writeText(code)
            .then(() => {
                setCopiedCodes(prev => ({ ...prev, [id]: true }));
                setTimeout(() => {
                    setCopiedCodes(prev => ({ ...prev, [id]: false }));
                }, 2000);
            })
            .catch(err => {
                console.error('Failed to copy code: ', err);
            });
    };
    
    return (
        <div className="my-linktree-container">
            <h2>My Affiliate Links</h2>
            
            <div className="linktree-intro">
                <p>Check out these amazing products I recommend! Use my links or codes below for special discounts.</p>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            {/* Admin controls */}
            {isAdmin && (
                <div className="admin-controls">
                    {!isAddingNew && !editingLink && (
                        <button 
                            className="add-affiliate-button"
                            onClick={startAddNew}
                        >
                            <FaPlus /> Add New Affiliate Link
                        </button>
                    )}
                    
                    {/* Form for adding/editing */}
                    {(isAddingNew || editingLink) && (
                        <form 
                            className="affiliate-form" 
                            onSubmit={editingLink ? handleUpdateLink : handleAddLink}
                        >
                            <h3>{editingLink ? 'Edit Affiliate Link' : 'Add New Affiliate Link'}</h3>
                            
                            <div className="form-group">
                                <label>Title:</label>
                                <input 
                                    type="text" 
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Description:</label>
                                <textarea 
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Discount Code (optional):</label>
                                <input 
                                    type="text" 
                                    name="code"
                                    value={formData.code}
                                    onChange={handleInputChange}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Discount Percentage (optional):</label>
                                <input 
                                    type="text" 
                                    name="discountPercent"
                                    value={formData.discountPercent}
                                    onChange={handleInputChange}
                                    placeholder="e.g., 30"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Affiliate Link URL:</label>
                                <input 
                                    type="url" 
                                    name="link"
                                    value={formData.link}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Product Image:</label>
                                <input
                                    type="file"
                                    id="affiliate-image-upload"
                                    accept="image/*"
                                    className="file-input"
                                />
                                {formData.imageUrl && (
                                    <div className="current-image">
                                        <p>Current image:</p>
                                        <img 
                                            src={formData.imageUrl} 
                                            alt="Current" 
                                            className="thumbnail" 
                                        />
                                    </div>
                                )}
                            </div>
                            
                            <div className="form-actions">
                                <button 
                                    type="submit" 
                                    className="save-button"
                                    disabled={uploading}
                                >
                                    {uploading ? 'Uploading...' : <><FaSave /> Save</>}
                                </button>
                                <button 
                                    type="button" 
                                    className="cancel-button"
                                    onClick={cancelEdit}
                                >
                                    <FaTimes /> Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}
            
            {/* Display affiliate links */}
            {loading ? (
                <div className="loading-spinner"></div>
            ) : (
                <div className="affiliate-links-container">
                    {affiliateLinks.length > 0 ? (
                        affiliateLinks.map(item => (
                            <div className="affiliate-card" key={item.id}>
                                <div className="affiliate-image">
                                    <img 
                                        src={item.imageUrl || '/placeholder-image.jpg'} 
                                        alt={item.title}
                                        onError={(e) => {
                                            e.target.src = '/placeholder-image.jpg';
                                        }}
                                    />
                                </div>
                                <div className="affiliate-content">
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                    {item.code && (
                                        <div className="affiliate-code-container">
                                            <div className="affiliate-code">
                                                <span>Code: </span>
                                                <code>{item.code}</code>
                                            </div>
                                            <button 
                                                className="code-copy-button"
                                                onClick={() => copyDiscountCode(item.code, item.id)}
                                                title="Copy discount code"
                                            >
                                                {copiedCodes[item.id] ? '✓ Copied!' : 'Copy'}
                                            </button>
                                            <p className="discount-note">
                                                Copy this code to use at checkout 
                                                {item.discountPercent ? ` for a whopping ${item.discountPercent}% off!` : ' for a special discount!'}
                                            </p>
                                        </div>
                                    )}
                                    <a 
                                        href={item.link} 
                                        className="affiliate-button"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Shop Now
                                    </a>
                                    
                                    {/* Admin edit/delete buttons */}
                                    {isAdmin && (
                                        <div className="admin-item-controls">
                                            <button 
                                                className="edit-button"
                                                onClick={() => handleEdit(item)}
                                                title="Edit this affiliate link"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button 
                                                className="delete-button"
                                                onClick={() => handleDeleteLink(item.id, item.imagePath)}
                                                title="Delete this affiliate link"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="no-links-message">
                            <p>No affiliate links have been added yet.</p>
                        </div>
                    )}
                </div>
            )}

            <div className="affiliate-disclaimer">
                <p>Disclosure: As an affiliate, I earn from qualifying purchases made through these links.</p>
            </div>
        </div>
    );
};

export default MyLinkTree; 